import type { DisplayDetail, MediaAsset } from "@led-signage/shared/management-api";
import { useState, type ReactNode } from "react";
import { api } from "./api/index.js";
import { useResource, type Resource } from "./api/useResource.js";
import { playbackEventView } from "./playback-events.js";

function Gate<T>({
  resource,
  children,
}: {
  resource: Resource<T>;
  children: (value: T) => ReactNode;
}) {
  if (resource.state === "loading") {
    return <p className="resource-note">読み込み中…</p>;
  }
  if (resource.state === "error") {
    return (
      <div className="inline-alert">
        <b>再生実績を読み込めませんでした</b>
        <span>{resource.message}</span>
      </div>
    );
  }
  return <>{children(resource.value)}</>;
}

export function PlaybackHistory() {
  const [displayId, setDisplayId] = useState("");
  const displays = useResource((signal) => api.listDisplays(signal), []);
  const media = useResource(
    (signal) => api.listMedia({ limit: 100 }, signal),
    [],
  );
  const events = useResource(
    (signal) =>
      api.listPlaybackEvents(
        { limit: 50, ...(displayId.length === 0 ? {} : { displayId }) },
        signal,
      ),
    [displayId],
  );

  const displayItems = displays.state === "ready" ? displays.value.items : [];
  const mediaItems = media.state === "ready" ? media.value.items : [];
  const displayByDevice = new Map(
    displayItems.flatMap((item: DisplayDetail) =>
      item.device === null ? [] : [[item.device.id, item.display.name] as const],
    ),
  );
  const mediaById = new Map(
    mediaItems.map((item: MediaAsset) => [item.id, item.originalName] as const),
  );

  return (
    <>
      <div className="filter-bar">
        <label className="audit-filter">
          LED
          <select
            aria-label="LEDで再生実績を絞り込む"
            value={displayId}
            disabled={displays.state !== "ready"}
            onChange={(event) => {
              setDisplayId(event.target.value);
            }}
          >
            <option value="">すべて</option>
            {displayItems.map((item: DisplayDetail) => (
              <option key={item.display.id} value={item.display.id}>
                {item.display.name}
              </option>
            ))}
          </select>
        </label>
        <span>最新50件</span>
      </div>

      <aside className="playback-notes" aria-label="再生実績の表示について">
        <p>デフォルト動画への切替は、スケジュール上の正常な動作の場合もあります。</p>
        <p>LED名を解決できない端末は、交換・退役済みの可能性があるため端末IDで表示します。</p>
      </aside>

      <Gate resource={events}>
        {(page) => {
          if (page.items.length === 0) {
            return (
              <p className="resource-note">
                {displayId.length === 0
                  ? "再生実績はまだありません。"
                  : "選択したLEDの再生実績はありません。"}
              </p>
            );
          }
          const rows = page.items.map((record) =>
            playbackEventView(
              record,
              (id) => displayByDevice.get(id) ?? `端末 ${id}`,
              (id) =>
                id === null
                  ? "不明"
                  : (mediaById.get(id) ?? `動画 ${id}`),
            ),
          );
          return (
            <section className="panel audit-table">
              <div className="playback-row playback-row--head">
                <span>日時（JST）</span>
                <span>LED / 端末</span>
                <span>事象</span>
                <span>動画 / 再生位置</span>
                <span>配信版</span>
              </div>
              {rows.map((row) => (
                <div className="playback-row" key={row.id}>
                  <time>
                    {row.date}<br />{row.time}
                  </time>
                  <span>{row.device}</span>
                  <span className={`playback-event playback-event--${row.tone}`}>
                    <b>{row.event}</b>
                    {row.detail !== null && <small>{row.detail}</small>}
                  </span>
                  <span>
                    <b>{row.media}</b>
                    <small>再生位置 {row.position}</small>
                  </span>
                  <code>{row.release}</code>
                </div>
              ))}
            </section>
          );
        }}
      </Gate>
    </>
  );
}
